import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  useToast,
  Box,
  Image,
  Divider,
  Checkbox,
  CheckboxGroup,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AddIcon } from '@chakra-ui/icons';
import { Bitacora } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  modo: 'new' | 'edit' | 'view';
  data?: Bitacora | null;
  onGuardar?: (data: Bitacora) => void;
}

const tiposServicio = ['Médico clínico', 'Médico traumático'];

const PERSONAL_BASE = [
  'Coordinador Luis Fernando Germán Sánchez',
  'Operativo Jose Roman Mejía Ortiz',
  'Operativo Braulio Mena Juárez',
];

const ModalBitacora = ({ isOpen, onClose, modo, data, onGuardar }: Props) => {
  const [form, setForm] = useState<Partial<Bitacora>>({
    personal: [],
    ambulancias: [],
  });
  const [personalExtra, setPersonalExtra] = useState<string[]>([]);
  const [nuevoPersonal, setNuevoPersonal] = useState('');
  const [tipoCustom, setTipoCustom] = useState(false);
  const toast = useToast();
  const isReadOnly = modo === 'view';

  const listaPersonal = [...PERSONAL_BASE, ...personalExtra];

  const agregarPersonal = () => {
    const nombre = nuevoPersonal.trim();
    if (!nombre) return;
    if (listaPersonal.includes(nombre)) {
      toast({ title: 'Ya existe en la lista', status: 'warning', duration: 2000, isClosable: true });
      return;
    }
    setPersonalExtra((prev) => [...prev, nombre]);
    setNuevoPersonal('');
  };

  const listaAmbulancias = [
    'PCT-11 - Unidad de Primer Contacto',
    'PCT-12 - Ambulancia RAM',
    'PCT-16 - Ambulancia FIAT',
    'PCT-09 - Cuatrimoto Polaris',
  ];

  useEffect(() => {
    if (data) {
      setForm({
        ...data,
        personal: safeParseJSON(data.personal) || [],
        ambulancias: safeParseJSON(data.ambulancias) || [],
        materialUtilizado: safeParseJSON(data.materialUtilizado) || [],
        evidenciaFotos: safeParseJSON(data.evidenciaFotos) || [],
        fechaServicio: data.fechaServicio?.split('T')[0] || '',
        horaSalida: data.horaSalida?.split('T')[1]?.slice(0, 5) || '',
        horaTermino: data.horaTermino?.split('T')[1]?.slice(0, 5) || '',
      });
      setTipoCustom(
        !!data.tipoServicio && !tiposServicio.includes(data.tipoServicio),
      );
    } else {
      setForm({
        fechaServicio: new Date().toISOString().split('T')[0], // yyyy-mm-dd
      });
      setTipoCustom(false);
    }
  }, [data]);

  function safeParseJSON(value: any) {
    try {
      const first = typeof value === 'string' ? JSON.parse(value) : value;
      return typeof first === 'string' ? JSON.parse(first) : first;
    } catch {
      return [];
    }
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const urls = Array.from(files)
      .slice(0, 8)
      .map((file) => URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, evidenciaFotos: urls }));
  };

  const handleGuardar = async () => {
    const camposRequeridos = [
      'fechaServicio',
      'personal',
      'ubicacion',
      'tipoServicio',
      'horaSalida',
      'horaTermino',
      'descripcionApoyo',
    ];
    const faltantes = camposRequeridos.filter(
      (c) => !form[c as keyof Bitacora],
    );
    if (faltantes.length > 0) {
      toast({
        title: 'Campos requeridos faltantes',
        description: faltantes.join(', '),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const url = 'https://totolac-backend.togui.com.mx/api/bitacoras';
    const formData = new FormData();

    formData.append('fechaServicio', form.fechaServicio!);
    formData.append('personal', JSON.stringify(form.personal || []));
    formData.append('municipio', form.municipio || '');
    formData.append('ubicacion', form.ubicacion || '');
    formData.append('direccion', form.direccion || '');
    formData.append('ambulancias', JSON.stringify(form.ambulancias || []));
    formData.append('tipoServicio', form.tipoServicio || '');
    formData.append('horaSalida', form.horaSalida || '');
    formData.append('horaTermino', form.horaTermino || '');
    formData.append('descripcionApoyo', form.descripcionApoyo || '');
    formData.append('detallesAtencion', form.detallesAtencion || '');
    formData.append('observaciones', form.observaciones || '');
    formData.append('apoyosAdicionales', form.apoyosAdicionales || '');
    formData.append('datosClinicos', form.datosClinicos || '');
    formData.append(
      'materialUtilizado',
      JSON.stringify(form.materialUtilizado || []),
    );
    formData.append('evidenciaMaterial', form.evidenciaMaterial || '');

    // Adjuntar imágenes reales si están disponibles
    const fileInput = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (fileInput && fileInput.files) {
      Array.from(fileInput.files)
        .slice(0, 8)
        .forEach((file) => {
          formData.append('evidenciaFotos', file);
        });
    }

    try {
      let response;

      if (modo === 'new') {
        response = await fetch(url, {
          method: 'POST',
          body: formData,
        });
      } else if (modo === 'edit' && form.id) {
        response = await fetch(`${url}/${form.id}`, {
          method: 'PUT',
          body: formData,
        });
      }

      if (!response || !response.ok) {
        throw new Error('Error al guardar la bitácora');
      }

      toast({
        title: 'Bitácora guardada exitosamente',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error al guardar',
        description: (error as Error).message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          position="relative"
        >
          {modo === 'view' ? 'Ver' : modo === 'edit' ? 'Editar' : 'Nueva'}{' '}
          Bitácora
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />

        <ModalBody>
          {/* SECCIÓN: Información General */}
          <FormLabel fontSize="lg" fontWeight="bold">
            Información General
          </FormLabel>
          <Divider mb={4} />
          <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Personal Participante</FormLabel>
              <CheckboxGroup
                colorScheme="purple"
                value={form.personal || []}
                onChange={(values: string[]) =>
                  setForm((prev) => ({
                    ...prev,
                    personal: values,
                  }))
                }
              >
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                  {listaPersonal.map((nombre) => (
                    <Checkbox
                      key={nombre}
                      value={nombre}
                      isDisabled={isReadOnly}
                    >
                      {nombre}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
              {!isReadOnly && (
                <HStack mt={3}>
                  <Input
                    placeholder="Agregar personal operativo..."
                    value={nuevoPersonal}
                    onChange={(e) => setNuevoPersonal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') agregarPersonal(); }}
                    size="sm"
                  />
                  <IconButton
                    aria-label="Agregar personal"
                    icon={<AddIcon />}
                    size="sm"
                    onClick={agregarPersonal}
                    colorScheme="purple"
                  />
                </HStack>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Ambulancias / Unidades</FormLabel>
              <CheckboxGroup
                colorScheme="teal"
                value={form.ambulancias || []}
                onChange={(values: string[]) =>
                  setForm((prev) => ({
                    ...prev,
                    ambulancias: values,
                  }))
                }
              >
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  {listaAmbulancias.map((a) => (
                    <Checkbox key={a} value={a} isDisabled={isReadOnly}>
                      {a}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Fecha del Servicio</FormLabel>
              <Input
                name="fechaServicio"
                type="date"
                value={form.fechaServicio || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Municipio de Atención</FormLabel>
              <Input
                name="municipio"
                value={form.municipio || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
                placeholder="Ej. Totolac"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Ubicación del Servicio</FormLabel>
              <Input
                name="ubicacion"
                value={form.ubicacion || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Dirección</FormLabel>
              <Input
                name="direccion"
                value={form.direccion || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </SimpleGrid>

          {/* SECCIÓN: Tipo y Horario */}
          <FormLabel fontSize="lg" fontWeight="bold" mt={8}>
            Tipo de Servicio y Horario
          </FormLabel>
          <Divider mb={4} />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Hora de Salida</FormLabel>
              <Input
                type="time"
                name="horaSalida"
                value={form.horaSalida || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Hora de Término</FormLabel>
              <Input
                type="time"
                name="horaTermino"
                value={form.horaTermino || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Tipo de Servicio</FormLabel>
              <Select
                value={tipoCustom ? '__custom__' : form.tipoServicio || ''}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setTipoCustom(true);
                    setForm((prev) => ({ ...prev, tipoServicio: '' }));
                  } else {
                    setTipoCustom(false);
                    setForm((prev) => ({
                      ...prev,
                      tipoServicio: e.target.value,
                    }));
                  }
                }}
                isDisabled={isReadOnly}
              >
                <option value="">Seleccione...</option>
                {tiposServicio.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
                <option value="__custom__">Otro (especificar)...</option>
              </Select>
              {tipoCustom && (
                <Input
                  mt={2}
                  name="tipoServicio"
                  placeholder="Especifique el tipo de servicio"
                  value={form.tipoServicio || ''}
                  onChange={handleChange}
                  isReadOnly={isReadOnly}
                />
              )}
            </FormControl>
          </SimpleGrid>

          {/* SECCIÓN: Atención Médica */}
          <FormLabel fontSize="lg" fontWeight="bold" mt={8}>
            Atención y Detalles Médicos
          </FormLabel>
          <Divider mb={4} />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Descripción del Apoyo Brindado</FormLabel>
              <Textarea
                name="descripcionApoyo"
                value={form.descripcionApoyo || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Detalles de Atención / Decisiones</FormLabel>
              <Textarea
                name="detallesAtencion"
                value={form.detallesAtencion || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Observaciones</FormLabel>
              <Textarea
                name="observaciones"
                value={form.observaciones || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>

            {/* <FormControl>
      <FormLabel>Apoyos Adicionales</FormLabel>
      <Textarea name="apoyosAdicionales" value={form.apoyosAdicionales || ''} onChange={handleChange} isReadOnly={isReadOnly} />
    </FormControl> */}

            <FormControl>
              <FormLabel>Datos Clínicos</FormLabel>
              <Textarea
                name="datosClinicos"
                value={form.datosClinicos || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </SimpleGrid>

          {/* SECCIÓN: Materiales y Evidencia */}
          <FormLabel fontSize="lg" fontWeight="bold" mt={8}>
            Materiales y Evidencia
          </FormLabel>
          <Divider mb={4} />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Material Utilizado (separado por coma)</FormLabel>
              <Input
                name="materialUtilizado"
                value={form.materialUtilizado?.join(', ') || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    materialUtilizado: e.target.value
                      .split(',')
                      .map((v) => v.trim()),
                  }))
                }
                isReadOnly={isReadOnly}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Evidencia Visual (máx 8)</FormLabel>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                isDisabled={isReadOnly}
              />
              <SimpleGrid columns={4} spacing={2} mt={2}>
                {form.evidenciaFotos?.map((src, i) => {
                  const fullUrl = src.startsWith('http')
                    ? src
                    : `https://totolac-backend.togui.com.mx${src}`;
                  return (
                    <Image
                      key={i}
                      src={fullUrl}
                      alt={`evidencia-${i}`}
                      boxSize="80px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  );
                })}
              </SimpleGrid>
            </FormControl>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter>
          {modo !== 'view' && (
            <Button onClick={handleGuardar} bg="#5b182b" color="white" _hover={{ bg: "#8a1f35" }} mr={3}>
              Guardar
            </Button>
          )}
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBitacora;
